/* A time-based typewriter over a complete grey guide, independent of scrolling. */
(() => {
  'use strict';
  if (window.LRSFounderTypewriter) { window.LRSFounderTypewriter.init(); return; }
  const selector = '[data-founder-typewriter]';
  const records = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const contrast = matchMedia('(prefers-contrast: more), (forced-colors: active)');
  const supported = !!(window.CSS?.supports('clip-path', 'polygon(0 0, 100% 0, 100% 100%, 0 100%)') && window.ResizeObserver);
  let frame = 0, fontsReady = !document.fonts || document.fonts.status === 'loaded';
  const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
  const enabled = record => supported && record.root.dataset.typewriterEnabled !== 'false' && document.body.dataset.lrsMotion !== 'false' && !reduced.matches && !contrast.matches && !window.Shopify?.designMode;
  const phase = (record, value) => { record.phase = value; record.root.dataset.typewriterPhase = value; };
  function clearInk(record) {
    record.ink?.remove(); record.ink = null;
    record.root.classList.remove('lrs-typewriter-ready');
  }
  function prepareInk(record, value) {
    if (!record.ink?.isConnected) {
      record.ink = document.createElement('span');
      record.ink.className = 'lrs-founder-portrait__ink';
      record.ink.setAttribute('aria-hidden', 'true');
      record.text.append(record.ink);
    }
    if (record.ink.textContent !== value) record.ink.textContent = value;
    record.root.classList.add('lrs-typewriter-ready');
  }
  function revealAll(record) {
    record.ink?.style.removeProperty('clip-path');
    record.root.classList.remove('lrs-typewriter-resetting');
  }
  function staticText(record) {
    revealAll(record); clearInk(record); record.ready = false; record.last = 0;
    phase(record, 'static'); record.root.dataset.typewriterCount = String(record.length || 0);
  }
  function paint(record) {
    record.root.dataset.typewriterCount = String(record.index);
    if (!record.ink) return;
    if (record.index >= record.length) { record.ink.style.removeProperty('clip-path'); return; }
    if (!record.index) { record.ink.style.clipPath = 'polygon(0 0,0 0,0 0)'; return; }
    const mark = record.marks[record.index - 1];
    record.ink.style.clipPath = `polygon(0 0,100% 0,100% ${mark.top}px,${mark.right}px ${mark.top}px,${mark.right}px ${mark.bottom}px,0 ${mark.bottom}px)`;
  }
  function graphemes(value) {
    if (window.Intl?.Segmenter) return [...new Intl.Segmenter(document.documentElement.lang || 'en', {granularity: 'grapheme'}).segment(value)].map(part => ({value: part.segment, start: part.index, end: part.index + part.segment.length}));
    let index = 0;
    return Array.from(value).map(part => { const start = index; index += part.length; return {value: part, start, end: index}; });
  }
  function measure(record) {
    if (!enabled(record)) { staticText(record); return; }
    if (!record.observed) { resize.observe(record.text); record.observed = true; }
    const node = record.text.firstChild;
    // The single original text node determines all line breaks. Its duplicate is
    // absolutely positioned, so the guide and ink share the exact justification.
    if (!node || node.nodeType !== Node.TEXT_NODE || [...record.text.childNodes].some(child => child !== node && child !== record.ink) || !node.nodeValue.trim()) { staticText(record); return; }
    const bounds = record.text.getBoundingClientRect();
    if (!bounds.width || !bounds.height) { staticText(record); return; }
    const parts = graphemes(node.nodeValue), rows = [], marks = [];
    const range = document.createRange();
    for (const part of parts) {
      range.setStart(node, part.start); range.setEnd(node, part.end);
      const rects = [...range.getClientRects()];
      const rect = rects[rects.length - 1];
      if (!rect || !Number.isFinite(rect.right)) { staticText(record); return; }
      const top = rect.top - bounds.top, bottom = rect.bottom - bounds.top;
      let row = rows[rows.length - 1];
      if (!row || Math.abs(row.top - top) > 2) { row = {top, bottom, index: rows.length}; rows.push(row); }
      else row.bottom = Math.max(row.bottom, bottom);
      marks.push({row, right: clamp(Math.ceil(rect.right - bounds.left + .4), 0, bounds.width), value: part.value});
    }
    range.detach();
    if (!marks.length) { staticText(record); return; }
    marks.forEach(mark => {
      const row = mark.row;
      mark.top = row.index ? Math.max(0, (rows[row.index - 1].bottom + row.top) / 2) : 0;
      mark.bottom = rows[row.index + 1] ? Math.min(bounds.height, (row.bottom + rows[row.index + 1].top) / 2) : bounds.height;
    });
    record.marks = marks; record.length = marks.length; record.index = Math.min(record.index, record.length);
    record.speed = clamp(Number(record.root.dataset.typewriterSpeed) || 20, 1, 100);
    record.pause = clamp(Number(record.root.dataset.typewriterPause) || 30, .1, 120) * 1000;
    const first = !record.ready;
    record.ready = true; record.last = 0;
    if (first) { record.index = 0; record.budget = 0; record.resume = 'typing'; phase(record, 'waiting'); }
    prepareInk(record, node.nodeValue);
    if (record.selection || record.focus) revealAll(record); else paint(record);
  }
  function live(record) { return record.ready && enabled(record) && !document.hidden && !record.selection && !record.focus; }
  function schedule() { if (!frame && [...records.values()].some(live)) frame = requestAnimationFrame(tick); }
  function tick(now) {
    frame = 0;
    records.forEach(record => {
      try {
        if (!record.root.isConnected) { remove(record); return; }
        if (!enabled(record)) { staticText(record); return; }
        if (!live(record)) { record.last = 0; return; }
        if (record.phase === 'waiting' || record.phase === 'paused') phase(record, record.resume || 'typing');
        const delta = record.last ? Math.min(100, now - record.last) : 0;
        record.last = now;
        // The writing clock continues through scrolling. The full grey guide
        // remains readable even when the paragraph extends beyond the viewport.
        if (record.phase === 'typing') {
          record.budget += delta;
          let count = 0;
          while (record.index < record.length && count < 30) {
            const char = record.marks[record.index].value;
            const cost = record.speed * (/[.!?]/.test(char) ? 5 : /[,;:]/.test(char) ? 2 : 1);
            if (record.budget < cost) break;
            record.budget -= cost; record.index++; count++;
          }
          paint(record);
          if (record.index === record.length) { record.remaining = record.pause; record.budget = 0; phase(record, 'reading'); }
        } else if (record.phase === 'reading') {
          record.remaining -= delta;
          if (record.remaining <= 0) { record.remaining = 340; record.root.classList.add('lrs-typewriter-resetting'); phase(record, 'resetting'); }
        } else if (record.phase === 'resetting') {
          record.remaining -= delta;
          if (record.remaining <= 0) {
            record.index = 0; record.budget = 0; paint(record);
            record.root.classList.remove('lrs-typewriter-resetting'); phase(record, 'typing');
          }
        }
      } catch { staticText(record); }
    });
    schedule();
  }
  function pause(record) {
    if (!record.ready || record.phase === 'static' || record.phase === 'paused' || record.phase === 'waiting') { record.last = 0; return; }
    record.resume = record.phase; record.last = 0; phase(record, 'paused');
  }
  function selectionChange() {
    const selection = document.getSelection();
    records.forEach(record => {
      let inside = false;
      if (selection && !selection.isCollapsed && selection.rangeCount) {
        try { inside = selection.getRangeAt(0).intersectsNode(record.text); } catch { /* Removed selections are harmless. */ }
      }
      if (inside) {
        record.selection = true; pause(record); revealAll(record);
        record.root.dataset.typewriterCount = String(record.length || 0);
      } else if (record.selection) {
        record.selection = false;
        if (record.ready) { record.index = record.length; record.remaining = record.pause; record.resume = 'reading'; phase(record, 'paused'); paint(record); }
      }
    });
    schedule();
  }
  const resize = supported ? new ResizeObserver(entries => {
    entries.forEach(entry => {
      const record = [...records.values()].find(item => item.text === entry.target);
      if (!record || !fontsReady) return;
      try { measure(record); } catch { staticText(record); }
    });
    schedule();
  }) : null;
  function remove(record) {
    resize?.unobserve(record.text);
    record.mutation?.disconnect();
    revealAll(record); clearInk(record); record.root.removeAttribute('data-typewriter-phase'); record.root.removeAttribute('data-typewriter-count');
    records.delete(record.root);
    if (!records.size && frame) { cancelAnimationFrame(frame); frame = 0; }
  }
  function init(scope = document) {
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach(root => {
      if (records.has(root)) return;
      const text = root.querySelector('.lrs-founder-portrait__text');
      if (!text) return;
      const record = {root, text, ink: null, ready: false, selection: false, focus: false, index: 0, length: 0, marks: [], last: 0, budget: 0, remaining: 0, resume: 'typing'};
      records.set(root, record); phase(record, 'waiting');
      record.mutation = new MutationObserver(changes => {
        // Only changes to the canonical copy restart its measurement. Changes
        // made while creating or updating the generated ink are ignored.
        const changed = changes.some(change => change.type === 'characterData' ? change.target.parentNode === text : change.target === text && [...change.addedNodes, ...change.removedNodes].some(node => node !== record.ink && !node.classList?.contains('lrs-founder-portrait__ink')));
        if (!changed) return;
        record.ready = false; record.index = 0;
        try { measure(record); } catch { staticText(record); }
        schedule();
      });
      record.mutation.observe(text, {childList: true, characterData: true, subtree: true});
      try {
        if (!enabled(record)) { staticText(record); return; }
        if (text.childNodes.length === 1 && text.firstChild.nodeType === Node.TEXT_NODE && text.firstChild.nodeValue.trim()) {
          prepareInk(record, text.firstChild.nodeValue);
          record.ink.style.clipPath = 'polygon(0 0,0 0,0 0)';
        }
        resize.observe(text); record.observed = true;
        if (fontsReady) measure(record);
      } catch { staticText(record); }
    });
    schedule();
  }
  function refresh() {
    records.forEach(record => { try { measure(record); } catch { staticText(record); } });
    init(); schedule();
  }
  function destroy(scope) { records.forEach(record => { if (record.root === scope || scope.contains(record.root)) remove(record); }); }
  window.LRSFounderTypewriter = {init, refresh, destroy};
  document.addEventListener('selectionchange', selectionChange);
  document.addEventListener('visibilitychange', () => { if (document.hidden) records.forEach(pause); schedule(); });
  document.addEventListener('focusin', event => {
    records.forEach(record => { if (record.text.contains(event.target)) { record.focus = true; pause(record); revealAll(record); } });
  });
  document.addEventListener('focusout', event => {
    records.forEach(record => { if (record.text.contains(event.target)) { record.focus = false; if (record.ready) { record.index = record.length; record.remaining = record.pause; record.resume = 'reading'; phase(record, 'paused'); paint(record); } } }); schedule();
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  reduced.addEventListener('change', refresh); contrast.addEventListener('change', refresh);
  addEventListener('pageshow', () => { records.forEach(record => { record.last = 0; }); schedule(); });
  if (document.fonts) {
    document.fonts.ready.then(() => { fontsReady = true; refresh(); }).catch(() => { fontsReady = true; refresh(); });
    document.fonts.addEventListener('loadingdone', refresh);
    // A delayed font service must not keep the paragraph waiting indefinitely.
    setTimeout(() => { if (!fontsReady) { fontsReady = true; refresh(); } }, 1500);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true}); else init();
})();
